// Playbook = Libertify's "Saved Settings" template — reusable storyboard +
// instance settings that can be applied to any project via `saved_settings_id`.
//
// Endpoint:  POST /{organization_id}/saved-settings
// Schema:    SavedSettingsCreationRequest (OpenAPI)

import { useMemo, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { CodeTabs } from '@/components/CodeTabs';
import { PayloadViewer } from '@/components/PayloadViewer';
import { ResponseViewer } from '@/components/ResponseViewer';
import { executeRequest } from '@/api/client';
import type { ApiResponseSnapshot, RequestSpec } from '@/types';
import type { PlaygroundProps } from './index';
import {
  CHAT_MODES,
  LANGUAGE_OPTIONS,
  MEDIA_GENERATION_MODES,
  OBJECTIVES,
  PUBLICATION_MODES,
  STORYBOARD_GENERATION_MODES,
  TONE_IDS,
  TRANSITIONS,
  type ChatMode,
  type MediaGenerationMode,
  type Objective,
  type PublicationMode,
  type StoryboardGenerationMode,
  type ToneId,
  type Transition,
} from './_constants';
import { OrgIdRequired } from './_shared';

// =============================================================================
// Helpers — strip nulls/empty so the JSON payload mirrors what an integrator
// would actually send (Cognito + FastAPI both accept omission for optional
// fields, and an empty payload reads cleaner in the docs).
// =============================================================================

function pruneNulls<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (typeof v === 'string' && v === '') continue;
    out[k] = v;
  }
  return out as T;
}

// =============================================================================
// Component
// =============================================================================

export function PlaybookPlayground({ mode }: PlaygroundProps) {
  const { env, organizationId, getValidAccessToken } = useAuth();

  // --- Request fields ---
  const [name, setName] = useState('Onboarding deck v1');
  const [description, setDescription] = useState('');

  // --- Storyboard settings ---
  const [language, setLanguage] = useState('en-US');
  const [duration, setDuration] = useState<number | null>(120);
  const [autoLength, setAutoLength] = useState(false);
  const [imageStyleDescription, setImageStyleDescription] = useState('');
  const [toneId, setToneId] = useState<ToneId>('Professional');
  const [selectedPages, setSelectedPages] = useState('');
  const [preserveOriginalDoc, setPreserveOriginalDoc] = useState(false);
  const [storyboardGenerationMode, setStoryboardGenerationMode] = useState<StoryboardGenerationMode>('Standard');
  const [brief, setBrief] = useState('');
  const [objectives, setObjectives] = useState<Objective[]>(['Onboard']);
  const [scriptTemplateId, setScriptTemplateId] = useState('');
  const [highlightsTemplateId, setHighlightsTemplateId] = useState('');

  // --- Media (instance) settings ---
  const [title, setTitle] = useState('');
  const [transitions, setTransitions] = useState<Transition>('Basic');
  const [mediaGenerationMode, setMediaGenerationMode] = useState<MediaGenerationMode>('Video');
  const [chatMode, setChatMode] = useState<ChatMode>('FullChat');
  const [includeDocumentAnimations, setIncludeDocumentAnimations] = useState(false);
  const [publicationMode, setPublicationMode] = useState<PublicationMode>('PublicLink');
  const [password, setPassword] = useState('');
  const [expirationTime, setExpirationTime] = useState('');
  const [instanceSlug, setInstanceSlug] = useState('');
  const [generateBrandkit, setGenerateBrandkit] = useState(true);
  const [generateImagesForSlides, setGenerateImagesForSlides] = useState(true);
  const [autoSelectMusic, setAutoSelectMusic] = useState(false);
  const [musicAssetId, setMusicAssetId] = useState('');
  const [videoSourcingEnabled, setVideoSourcingEnabled] = useState(true);
  const [imageExtractionEnabled, setImageExtractionEnabled] = useState(false);
  const [brandKitId, setBrandKitId] = useState('');
  const [contactId, setContactId] = useState('');
  const [ctaGroupId, setCtaGroupId] = useState('');
  const [themeId, setThemeId] = useState('');

  // --- Live execution ---
  const [response, setResponse] = useState<ApiResponseSnapshot | null>(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  const toggleObjective = (obj: Objective) => {
    setObjectives((prev) => (prev.includes(obj) ? prev.filter((o) => o !== obj) : [...prev, obj]));
  };

  // Build the request spec — single source of truth for payload + code + run.
  const spec: RequestSpec = useMemo(() => {
    const storyboard_settings = pruneNulls({
      language: language || null,
      duration: autoLength ? null : duration,
      tone_id: toneId,
      storyboard_generation_mode: storyboardGenerationMode,
      brief: brief || null,
      image_style_description: imageStyleDescription || null,
      objectives: objectives.length > 0 ? objectives : null,
      selected_pages: selectedPages || null,
      preserve_original_doc: preserveOriginalDoc ? true : null,
      script_template_id: scriptTemplateId || null,
      highlights_template_id: highlightsTemplateId || null,
    });

    const media_settings = pruneNulls({
      title: title || null,
      transitions,
      media_generation_mode: mediaGenerationMode,
      chat_mode: chatMode,
      include_document_animations: includeDocumentAnimations ? true : null,
      publication_mode: publicationMode,
      password: publicationMode === 'PasswordProtected' ? password || null : null,
      expiration_time: expirationTime ? new Date(expirationTime).toISOString() : null,
      instance_slug: instanceSlug || null,
      generate_brandkit: generateBrandkit,
      generate_images_for_slides: generateImagesForSlides,
      auto_select_music: autoSelectMusic ? true : null,
      music_asset_id: musicAssetId || null,
      video_sourcing_enabled: videoSourcingEnabled,
      image_extraction_enabled: imageExtractionEnabled,
      brand_kit_id: brandKitId || null,
      contact_id: contactId || null,
      cta_group_id: ctaGroupId || null,
      theme_id: themeId || null,
    });

    const orgSegment = organizationId || '{organization_id}';
    return {
      method: 'POST',
      path: `/media/${orgSegment}/saved-settings`,
      body: pruneNulls({
        name: name.trim() || 'Untitled playbook',
        description: description.trim() || null,
        storyboard_settings: Object.keys(storyboard_settings).length > 0 ? storyboard_settings : null,
        media_settings: Object.keys(media_settings).length > 0 ? media_settings : null,
      }),
    };
  }, [
    organizationId,
    name,
    description,
    language,
    duration,
    autoLength,
    toneId,
    storyboardGenerationMode,
    brief,
    imageStyleDescription,
    objectives,
    selectedPages,
    preserveOriginalDoc,
    scriptTemplateId,
    highlightsTemplateId,
    title,
    transitions,
    mediaGenerationMode,
    chatMode,
    includeDocumentAnimations,
    publicationMode,
    password,
    expirationTime,
    instanceSlug,
    generateBrandkit,
    generateImagesForSlides,
    autoSelectMusic,
    musicAssetId,
    videoSourcingEnabled,
    imageExtractionEnabled,
    brandKitId,
    contactId,
    ctaGroupId,
    themeId,
  ]);

  const handleRun = async () => {
    setRunError(null);
    setRunning(true);
    try {
      if (!organizationId) throw new Error('Set your organization_id in the top bar first.');
      const token = await getValidAccessToken();
      if (!token) throw new Error('Not signed in.');
      setResponse(await executeRequest(env, spec, token));
    } catch (e) {
      setRunError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  const durationLabel =
    duration == null
      ? '—'
      : duration < 60
        ? `${duration}s`
        : duration % 60 === 0
          ? `${duration / 60} min`
          : `${Math.floor(duration / 60)}m ${duration % 60}s`;
  const durationPct = duration ? ((duration - 30) / (600 - 30)) * 100 : 0;

  return (
    <div className="lp-grid lp-grid-cols-1 xl:lp-grid-cols-[minmax(0,1.35fr)_minmax(360px,440px)] lp-gap-7">
      {/* ============== LEFT: Form ============== */}
      <div className="lp-space-y-5 lp-min-w-0">
        <div>
          <h1 className="lp-text-2xl lp-font-bold lp-text-ink-900 lp-tracking-tight">Playbook builder</h1>
          <p className="lp-text-sm lp-text-ink-500 lp-mt-1.5 lp-leading-relaxed">
            <code className="lp-bg-ink-100 lp-text-ink-700 lp-px-1.5 lp-py-0.5 lp-rounded lp-font-mono lp-text-[11.5px]">SavedSettingsCreationRequest</code> →{' '}
            <code className="lp-bg-ink-100 lp-text-ink-700 lp-px-1.5 lp-py-0.5 lp-rounded lp-font-mono lp-text-[11.5px]">POST /{'{organization_id}'}/saved-settings</code>.
            Reuse the returned <code className="lp-bg-ink-100 lp-text-ink-700 lp-px-1.5 lp-py-0.5 lp-rounded lp-font-mono lp-text-[11.5px]">id</code> in any project&apos;s{' '}
            <code className="lp-bg-ink-100 lp-text-ink-700 lp-px-1.5 lp-py-0.5 lp-rounded lp-font-mono lp-text-[11.5px]">saved_settings_id</code>.
          </p>
        </div>

        <OrgIdRequired organizationId={organizationId} />

        {/* Name & description */}
        <Card>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Playbook name"
            className="lp-w-full lp-text-lg lp-font-semibold lp-text-ink-900 lp-bg-transparent lp-border-none lp-outline-none placeholder:lp-text-ink-300"
          />
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            className="lp-w-full lp-text-sm lp-text-ink-500 lp-bg-transparent lp-border-none lp-outline-none lp-mt-1 placeholder:lp-text-ink-300"
          />
        </Card>

        {/* ===================================================================
             STORYBOARD SETTINGS
             =================================================================== */}
        <SectionHeader title="Storyboard settings" subtitle="ProjectStoryboardSettingsRequest" />

        <Section title="Language">
          <div className="lp-flex lp-flex-wrap lp-gap-2">
            {LANGUAGE_OPTIONS.map((l) => (
              <Pill key={l.code} active={language === l.code} onClick={() => setLanguage(l.code)}>
                <span>{l.flag}</span>
                {l.label}
              </Pill>
            ))}
          </div>
        </Section>

        <Card>
          <div className="lp-flex lp-items-center lp-justify-between lp-mb-4">
            <div>
              <h3 className="lp-text-xs lp-font-semibold lp-text-ink-400 lp-uppercase lp-tracking-wider">
                Target duration
              </h3>
              <p className="lp-text-xs lp-text-ink-400 lp-mt-0.5">
                {autoLength ? 'Field omitted from payload' : 'Approximate length, in seconds'}
              </p>
            </div>
            <div className="lp-flex lp-items-center lp-gap-3">
              <Toggle checked={autoLength} onChange={setAutoLength} label="Auto" />
              {!autoLength && (
                <span className="lp-text-xl lp-font-bold lp-text-ink-900 lp-tabular-nums">{durationLabel}</span>
              )}
            </div>
          </div>
          {!autoLength && (
            <>
              <div className="lp-relative">
                <div className="lp-relative lp-h-2 lp-bg-ink-200 lp-rounded-full">
                  <div
                    className="lp-absolute lp-inset-y-0 lp-left-0 lp-rounded-full lp-bg-gradient-to-r lp-from-brand-500 lp-to-brand-700"
                    style={{ width: `${durationPct}%` }}
                  />
                </div>
                <input
                  type="range"
                  min={30}
                  max={600}
                  step={10}
                  value={duration ?? 120}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className="lp-absolute lp-inset-0 lp-w-full lp-opacity-0 lp-cursor-pointer"
                  style={{ height: '24px', top: '-8px' }}
                />
              </div>
              <div className="lp-flex lp-justify-between lp-text-[11px] lp-text-ink-400 lp-mt-3 lp-font-medium">
                <span>30s</span><span>2 min</span><span>5 min</span><span>10 min</span>
              </div>
            </>
          )}
        </Card>

        <Section title="Tone" subtitle="ToneId enum">
          <div className="lp-flex lp-flex-wrap lp-gap-2">
            {TONE_IDS.map((t) => (
              <Pill key={t} active={toneId === t} onClick={() => setToneId(t)}>
                {t}
              </Pill>
            ))}
          </div>
        </Section>

        <Section title="Storyboard generation mode" subtitle="StoryboardGenerationMode enum">
          <div className="lp-grid lp-grid-cols-3 lp-gap-2">
            {STORYBOARD_GENERATION_MODES.map((m) => (
              <Pill key={m} active={storyboardGenerationMode === m} onClick={() => setStoryboardGenerationMode(m)}>
                {m}
              </Pill>
            ))}
          </div>
        </Section>

        <Section title="Objectives" subtitle="Objective[] (multi-select)">
          <div className="lp-grid lp-grid-cols-2 sm:lp-grid-cols-4 lp-gap-2">
            {OBJECTIVES.map((obj) => (
              <Pill key={obj} active={objectives.includes(obj)} onClick={() => toggleObjective(obj)}>
                {obj}
              </Pill>
            ))}
          </div>
        </Section>

        <Card>
          <Label>Brief</Label>
          <Hint>Short context for the AI: audience, key message, tone.</Hint>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={3}
            placeholder="e.g. 'For new sales hires. Friendly, welcoming tone.'"
            className="lp-w-full lp-mt-2 lp-text-sm lp-bg-transparent lp-border-none lp-outline-none lp-resize-none placeholder:lp-text-ink-300"
          />
        </Card>

        <Card>
          <Label>Image style</Label>
          <Hint>Visual style for AI-generated images.</Hint>
          <input
            type="text"
            value={imageStyleDescription}
            onChange={(e) => setImageStyleDescription(e.target.value)}
            placeholder="watercolor, 3D render, corporate photography…"
            className="lp-w-full lp-mt-2 lp-text-sm lp-bg-transparent lp-border-none lp-outline-none placeholder:lp-text-ink-300"
          />
        </Card>

        <div className="lp-grid lp-grid-cols-1 sm:lp-grid-cols-2 lp-gap-3">
          <Card>
            <Label>Selected pages</Label>
            <Hint>e.g. "1-3,5,7-10"</Hint>
            <input
              type="text"
              value={selectedPages}
              onChange={(e) => setSelectedPages(e.target.value)}
              placeholder="1-3,5,7-10"
              className="lp-w-full lp-mt-2 lp-text-sm lp-bg-transparent lp-border-none lp-outline-none placeholder:lp-text-ink-300"
            />
          </Card>
          <Card>
            <div className="lp-flex lp-items-center lp-justify-between">
              <div>
                <Label>Preserve original doc</Label>
                <Hint>Keep original document layout</Hint>
              </div>
              <Toggle checked={preserveOriginalDoc} onChange={setPreserveOriginalDoc} />
            </div>
          </Card>
        </div>

        <div className="lp-grid lp-grid-cols-1 sm:lp-grid-cols-2 lp-gap-3">
          <Card>
            <Label>Script template ID</Label>
            <input
              type="text"
              value={scriptTemplateId}
              onChange={(e) => setScriptTemplateId(e.target.value)}
              placeholder="Optional"
              className="lp-w-full lp-mt-2 lp-text-sm lp-font-mono lp-bg-transparent lp-border-none lp-outline-none placeholder:lp-text-ink-300"
            />
          </Card>
          <Card>
            <Label>Highlights template ID</Label>
            <input
              type="text"
              value={highlightsTemplateId}
              onChange={(e) => setHighlightsTemplateId(e.target.value)}
              placeholder="Optional"
              className="lp-w-full lp-mt-2 lp-text-sm lp-font-mono lp-bg-transparent lp-border-none lp-outline-none placeholder:lp-text-ink-300"
            />
          </Card>
        </div>

        {/* ===================================================================
             MEDIA / INSTANCE SETTINGS
             =================================================================== */}
        <SectionHeader title="Media settings" subtitle="ProjectInstanceSettingsRequest" />

        <Section title="Output mode" subtitle="MediaGenerationMode enum">
          <div className="lp-grid lp-grid-cols-4 lp-gap-2">
            {MEDIA_GENERATION_MODES.map((m) => (
              <Pill key={m} active={mediaGenerationMode === m} onClick={() => setMediaGenerationMode(m)}>
                {m}
              </Pill>
            ))}
          </div>
        </Section>

        <Section title="Chat mode" subtitle="ChatMode enum">
          <div className="lp-grid lp-grid-cols-3 lp-gap-2">
            {CHAT_MODES.map((m) => (
              <Pill key={m} active={chatMode === m} onClick={() => setChatMode(m)}>
                {m === 'FullChat' ? 'Full Chat' : m === 'SourcesOnly' ? 'Sources Only' : 'Disabled'}
              </Pill>
            ))}
          </div>
        </Section>

        <Section title="Transitions" subtitle="Transitions enum">
          <div className="lp-grid lp-grid-cols-2 lp-gap-2">
            {TRANSITIONS.map((t) => (
              <Pill key={t} active={transitions === t} onClick={() => setTransitions(t)}>
                {t}
              </Pill>
            ))}
          </div>
        </Section>

        <Section title="Sharing & access" subtitle="PublicationMode enum">
          <div className="lp-bg-white lp-border lp-border-ink-200 lp-rounded-2xl lp-overflow-hidden">
            <div className="lp-grid lp-grid-cols-3 lp-gap-1 lp-p-2">
              {PUBLICATION_MODES.map((m) => (
                <Pill key={m} active={publicationMode === m} onClick={() => setPublicationMode(m)}>
                  {m === 'PublicLink' ? 'Public Link' : m === 'PinByEmail' ? 'Pin by Email' : 'Password'}
                </Pill>
              ))}
            </div>
            {publicationMode === 'PasswordProtected' && (
              <div className="lp-px-4 lp-pb-4">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter a password…"
                  className="lp-w-full lp-bg-ink-50 lp-rounded-xl lp-px-3 lp-py-2.5 lp-text-sm lp-border lp-border-ink-200 focus:lp-outline-none focus:lp-border-brand-400"
                />
              </div>
            )}
            <div className="lp-border-t lp-border-ink-100 lp-px-4 lp-py-3 lp-flex lp-items-center lp-justify-between">
              <div>
                <div className="lp-text-sm lp-font-medium">Expiration</div>
                <div className="lp-text-xs lp-text-ink-400">ISO 8601 datetime</div>
              </div>
              <input
                type="datetime-local"
                value={expirationTime}
                onChange={(e) => setExpirationTime(e.target.value)}
                className="lp-text-sm lp-text-brand-600 lp-bg-transparent lp-border-none lp-outline-none lp-text-right"
              />
            </div>
          </div>
        </Section>

        <Card>
          <div className="lp-grid lp-grid-cols-1 sm:lp-grid-cols-2 lp-gap-y-4 lp-gap-x-6">
            <SwitchRow label="Generate brand kit" hint="generate_brandkit" checked={generateBrandkit} onChange={setGenerateBrandkit} />
            <SwitchRow label="Generate slide images" hint="generate_images_for_slides" checked={generateImagesForSlides} onChange={setGenerateImagesForSlides} />
            <SwitchRow label="Auto-select music" hint="auto_select_music" checked={autoSelectMusic} onChange={setAutoSelectMusic} />
            <SwitchRow label="Document animations" hint="include_document_animations" checked={includeDocumentAnimations} onChange={setIncludeDocumentAnimations} />
            <SwitchRow label="Stock video sourcing" hint="video_sourcing_enabled" checked={videoSourcingEnabled} onChange={setVideoSourcingEnabled} />
            <SwitchRow label="Image extraction" hint="image_extraction_enabled" checked={imageExtractionEnabled} onChange={setImageExtractionEnabled} />
          </div>
        </Card>

        <Section title="Bindings" subtitle="Reference resources by ID — the playground does not fetch them">
          <div className="lp-grid lp-grid-cols-1 sm:lp-grid-cols-2 lp-gap-3">
            <IdInput label="Title (display name)" value={title} onChange={setTitle} placeholder="Optional override" />
            <IdInput label="Instance slug" value={instanceSlug} onChange={setInstanceSlug} placeholder="instance_slug" />
            <IdInput label="brand_kit_id" value={brandKitId} onChange={setBrandKitId} />
            <IdInput label="contact_id" value={contactId} onChange={setContactId} />
            <IdInput label="cta_group_id" value={ctaGroupId} onChange={setCtaGroupId} />
            <IdInput label="theme_id" value={themeId} onChange={setThemeId} />
            <IdInput label="music_asset_id" value={musicAssetId} onChange={setMusicAssetId} />
          </div>
        </Section>

        {/* Run button (live mode only) */}
        {mode === 'live' && (
          <button
            onClick={handleRun}
            disabled={running || !organizationId}
            className="lp-btn-primary lp-w-full lp-py-3 lp-text-sm lp-font-bold"
            style={{ borderRadius: 12 }}
          >
            {running ? 'Sending…' : `Send POST /${organizationId || '{organization_id}'}/saved-settings`}
          </button>
        )}
      </div>

      {/* ============== RIGHT: Live payload + code + response ============== */}
      <div className="lp-space-y-5 xl:lp-sticky xl:lp-top-6 lp-self-start lp-min-w-0">
        <SidePanelHeader title="Request body" subtitle="Updates live as you fill the form" />
        <PayloadViewer spec={spec} />

        <SidePanelHeader title="Code samples" subtitle="Copy-paste in your stack of choice" />
        <CodeTabs env={env} spec={spec} hasAuth />

        {mode === 'live' && (
          <>
            <SidePanelHeader title="Response" subtitle="Real API response from your account" />
            <ResponseViewer response={response} loading={running} error={runError} />
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Local UI helpers (only used by Playbook — keeps the JSX above readable)
// =============================================================================

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="lp-p-5 lp-shadow-card"
      style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 22 }}
    >
      {children}
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="lp-text-[11px] lp-font-semibold lp-text-ink-500 lp-uppercase lp-tracking-[0.08em]">{title}</h2>
      {subtitle && <p className="lp-text-[11px] lp-text-ink-400 lp-mt-0.5 lp-mb-3 lp-font-mono">{subtitle}</p>}
      {!subtitle && <div className="lp-mb-3" />}
      {children}
    </div>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="lp-pt-4 lp-border-t lp-border-ink-200">
      <h2 className="lp-text-base lp-font-bold lp-text-ink-900">{title}</h2>
      {subtitle && <p className="lp-text-[11px] lp-text-ink-400 lp-mt-0.5 lp-font-mono">{subtitle}</p>}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <span className="lp-text-[11px] lp-font-semibold lp-text-ink-500 lp-uppercase lp-tracking-[0.08em]">{children}</span>;
}

function Hint({ children }: { children: React.ReactNode }) {
  return <p className="lp-text-[11px] lp-text-ink-400 lp-mt-0.5">{children}</p>;
}

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`lp-flex lp-items-center lp-justify-center lp-gap-2 lp-px-3.5 lp-py-2 lp-rounded-lg lp-text-[13px] lp-font-medium lp-transition-all ${
        active
          ? 'lp-bg-brand-600 lp-text-white lp-shadow-sm'
          : 'lp-bg-white lp-border lp-border-ink-200 lp-text-ink-700 hover:lp-bg-ink-50 hover:lp-border-ink-300'
      }`}
    >
      {children}
    </button>
  );
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <label className="lp-inline-flex lp-items-center lp-gap-2 lp-cursor-pointer">
      {label && <span className="lp-text-sm lp-text-ink-600 lp-font-medium">{label}</span>}
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`lp-relative lp-w-10 lp-h-5.5 lp-rounded-full lp-transition-colors ${
          checked ? 'lp-bg-brand-500' : 'lp-bg-ink-300'
        }`}
        style={{ height: '22px', width: '40px' }}
      >
        <span
          className={`lp-absolute lp-top-0.5 lp-left-0.5 lp-bg-white lp-rounded-full lp-shadow lp-transition-transform ${
            checked ? 'lp-translate-x-[18px]' : 'lp-translate-x-0'
          }`}
          style={{ height: '18px', width: '18px' }}
        />
      </button>
    </label>
  );
}

function SwitchRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="lp-flex lp-items-center lp-justify-between lp-gap-4">
      <div>
        <div className="lp-text-sm lp-font-medium lp-text-ink-900">{label}</div>
        {hint && <div className="lp-text-[11px] lp-text-ink-400 lp-font-mono">{hint}</div>}
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );
}

function IdInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="lp-block">
      <span className="lp-text-[11px] lp-font-mono lp-text-ink-500">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Optional'}
        className="lp-block lp-w-full lp-mt-1 lp-bg-white lp-border lp-border-ink-200 lp-rounded-xl lp-px-3 lp-py-2 lp-text-sm lp-font-mono lp-text-ink-900 placeholder:lp-text-ink-400 lp-focus-ring lp-transition-shadow"
      />
    </label>
  );
}

function SidePanelHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div>
      <div className="lp-text-[11px] lp-font-semibold lp-text-ink-500 lp-uppercase lp-tracking-[0.08em]">{title}</div>
      {subtitle && <div className="lp-text-[11px] lp-text-ink-400 lp-mt-0.5">{subtitle}</div>}
    </div>
  );
}
